import { useEffect, useState } from 'react'
import { ApiError, api, type ConnectResult, type ConnectStep } from '../api'
import { haptic } from '../telegram'

/**
 * Connecting the customer's Telegram account, step by step: the consent and the phone number, the code
 * Telegram sends, the cloud password when the account has one.
 *
 * What is typed goes to our server, which hands it to Telegram and keeps only the resulting session,
 * sealed; nothing typed is stored or logged, and a login for a different Telegram account than the one
 * that opened the app is thrown away. The consent is deliberately blunt: a Telegram session is the whole
 * account, and a customer surprised later was not asked properly.
 */
export function ConnectCard({
  available,
  onConnected,
}: {
  /** Connecting works on this server at all. */
  available: boolean
  /** Called once the account is connected, with the server's word on the game pass. */
  onConnected: (result: ConnectResult) => Promise<void>
}) {
  const [step, setStep] = useState<ConnectStep>('phone')
  const [consent, setConsent] = useState(false)
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // A login waits ten minutes for its next step: reopening the app in the middle picks it up again.
  useEffect(() => {
    let cancelled = false
    api
      .getConnect()
      .then((status) => {
        if (!cancelled && (status.step === 'code' || status.step === 'password')) setStep(status.step)
      })
      .catch(() => {
        /* the phone step is the safe default */
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function submit(call: () => Promise<ConnectResult>) {
    setBusy(true)
    setError(null)
    try {
      const result = await call()
      // The inputs are cleared at every answer: a code or password has no business lingering on screen.
      setCode('')
      setPassword('')
      if (result.step === 'done') {
        haptic('ok')
        await onConnected(result)
        return
      }
      if (result.error) {
        haptic('error')
        setError(result.error)
      } else {
        haptic('tap')
      }
      setStep(result.step)
    } catch (e) {
      haptic('error')
      setError(messageFor(e))
    } finally {
      setBusy(false)
    }
  }

  async function cancel() {
    setBusy(true)
    try {
      await api.connectCancel()
    } catch {
      /* nothing was stored; the attempt also expires on its own */
    } finally {
      setCode('')
      setPassword('')
      setError(null)
      setStep('phone')
      setBusy(false)
    }
  }

  if (!available) {
    return (
      <section className="card">
        <div className="title">Аккаунт не подключён</div>
        <p className="muted note">Подключение аккаунтов на сервере сейчас не настроено. Напишите в поддержку.</p>
      </section>
    )
  }

  return (
    <section className="card">
      <div className="row">
        <span className="dot bad" />
        <div className="grow">
          <div className="title">Подключите игровой аккаунт</div>
        </div>
      </div>

      {error && <p className="banner bad">{error}</p>}

      {step === 'phone' && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault()
            if (!consent || !phone.trim()) return
            void submit(() => api.connectStart(phone, consent))
          }}
        >
          <div className="consent">
            <p>
              Чтобы бот мог играть за вас, ему нужно самому входить в игру. Игра выдаёт пропуск всего на 24 часа и не
              умеет его продлевать, поэтому пропуск приходится получать заново каждый день. Единственный способ — вход
              в ваш аккаунт Telegram.
            </p>
            <p className="title">Что это значит, честно:</p>
            <ul>
              <li>Мы сохраним у себя ключ сессии вашего Telegram. Он даёт доступ ко всему аккаунту, а не только к игре.</li>
              <li>Ключ хранится в зашифрованном виде, но доверие всё равно требуется — оценивайте это решение как таковое.</li>
              <li>
                Telegram пришлёт вам код и предупредит «никому не сообщайте». Вы введёте его здесь — это осознанный выбор.
              </li>
              <li>Сессия появится у вас в Telegram: Настройки → Устройства. Завершить её можно там в любой момент.</li>
              <li>Кнопка «Отключить аккаунт» здесь удаляет ключ у нас.</li>
            </ul>
            <p className="muted">Если это вас не устраивает — это нормально, просто не продолжайте.</p>
          </div>

          <label className="check">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={busy} />
            <span>Понимаю и согласен</span>
          </label>

          <input
            className="field"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+7 900 123-45-67"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={busy || !consent}
            aria-label="Номер телефона аккаунта Telegram"
          />
          <p className="muted small">Номер того аккаунта Telegram, из которого вы открыли это приложение.</p>

          <button className="button primary" type="submit" disabled={busy || !consent || !phone.trim()}>
            {busy ? 'Отправляем…' : 'Получить код'}
          </button>
        </form>
      )}

      {step === 'code' && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault()
            if (!code.trim()) return
            void submit(() => api.connectCode(code))
          }}
        >
          <p className="note">
            Telegram прислал код в чат «Telegram» (не SMS). Введите его здесь — больше никому его не сообщайте.
          </p>
          <input
            className="field"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="Код"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={busy}
            aria-label="Код из Telegram"
            autoFocus
          />
          <button className="button primary" type="submit" disabled={busy || !code.trim()}>
            {busy ? 'Проверяем…' : 'Подтвердить'}
          </button>
          <button className="button" type="button" onClick={() => void cancel()} disabled={busy}>
            Отмена
          </button>
        </form>
      )}

      {step === 'password' && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault()
            if (!password) return
            void submit(() => api.connectPassword(password))
          }}
        >
          <p className="note">У аккаунта включена двухэтапная аутентификация — введите облачный пароль Telegram.</p>
          <input
            className="field"
            type="password"
            autoComplete="current-password"
            placeholder="Облачный пароль"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            aria-label="Облачный пароль Telegram"
            autoFocus
          />
          <button className="button primary" type="submit" disabled={busy || !password}>
            {busy ? 'Входим…' : 'Войти'}
          </button>
          <button className="button" type="button" onClick={() => void cancel()} disabled={busy}>
            Отмена
          </button>
        </form>
      )}
    </section>
  )
}

/** The server's own words where it gave them; otherwise what went wrong in general terms. */
function messageFor(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.isRateLimited) {
      const minutes = e.retryAfterSeconds ? Math.max(1, Math.ceil(e.retryAfterSeconds / 60)) : null
      return minutes
        ? `Слишком много попыток — попробуйте снова через ${minutes} мин.`
        : 'Слишком много попыток — попробуйте позже.'
    }
    if (e.isOffline) return 'Нет связи с сервером — попробуйте ещё раз.'
    if (e.isServerRestarting && e.code !== 'connect_unavailable') return 'Сервер перезапускается, попробуйте через минуту.'
    if (e.message && e.message !== e.code) return e.message
  }
  return 'Не получилось — попробуйте ещё раз.'
}
